<script setup lang="ts">
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { EntrGroup } from '@/domain/types';
import { contentImageUrlWithSize } from '@/kiosk-core/net/contentImageUrl';
import { keyedImageRetry } from '@/lib/imageRetry';
import {
  reportNewGroupsOutsideViewport,
  reportPhotoImageFailure,
} from '@/telemetry/photoAvailability';
import EntrChrome from '@/ui/components/EntrChrome.vue';
import EntrGridPanel from '@/ui/components/EntrGridPanel.vue';
import EntrIcon from '@/ui/components/EntrIcon.vue';
import { useAttractController } from './controller';
import type { AttractScreenEvents, AttractScreenProps } from './types';

const props = withDefaults(defineProps<AttractScreenProps>(), {
  canOpenScan: true,
});
const emit = defineEmits<AttractScreenEvents>();
const controller = useAttractController(emit, props);

// Thumbnail tier for the browse-grid tile (~300px cell): the group
// portraits are multi-MB originals; the print path is untouched (UUIDs).
const coverSrc = (group: EntrGroup): string =>
  contentImageUrlWithSize(group.coverUrl, 640, 85);
const { t } = useI18n();
// Bounded retry with cache-bust: a transient blip no longer strands a tile
// on the skeleton for the whole session (or feeds the visual harness a
// silently-broken screenshot). coverRetry.isErrored is true only after ALL
// attempts failed.
function coverImageKey(group: EntrGroup): string {
  return `${group.id}:${group.coverUrl}`;
}

const coverRetry = keyedImageRetry(coverImageKey, coverSrc, {
  onExhausted(key, attempts) {
    const group = props.groups.find((item) => coverImageKey(item) === key);
    if (group) {
      reportPhotoImageFailure({
        group_id: group.id,
        image_id: group.coverImageUuid ?? null,
        photo_type: 'group-cover',
        attempts,
      });
    }
  },
});

watch(
  () => props.groups,
  (groups, previous) => {
    if (previous.length === 0) return;
    const previousIds = new Set(previous.map((group) => group.id));
    reportNewGroupsOutsideViewport(
      groups
        .filter((group) => !previousIds.has(group.id))
        .map((group) => group.id)
    );
  },
  { flush: 'post' }
);

// Opening-night kill-switch: when group browse is disabled the entire
// attract screen becomes a single tap-to-scan target. Per Vincent the
// canonical flow is group-browse-first, but ENTR's /api/photo_groups
// endpoint wasn't ready for 2026-05-22 — the scan-only fallback keeps
// the kiosk usable without rendering mock data in production.
const GROUP_BROWSE_ENABLED =
  import.meta.env.VITE_ENTR_GROUP_BROWSE_ENABLED === 'true';

function groupKey(group: EntrGroup): string {
  return group.id;
}

function groupLookupId(group: EntrGroup): string {
  return group.lookupId ?? group.id;
}
</script>

<template>
  <section
    v-if="GROUP_BROWSE_ENABLED"
    class="screen attract-screen attract-browse"
    data-testid="screen-attract"
  >
    <header class="intro">
      <h1 class="t-headline headline reveal" style="--reveal-order: 0">{{ t('attract.headline') }}</h1>
      <p class="t-body-lg subhead reveal" style="--reveal-order: 1">{{ t('attract.subhead') }}</p>
    </header>

    <!-- @vue-generic {EntrGroup} -->
    <EntrGridPanel
      class="group-grid reveal"
      style="--reveal-order: 2"
      :items="groups"
      :columns="2"
      :rows="3"
      :ariaLabel="t('attract.groupsLabel')"
      test-id="groups-grid"
      :item-key="groupKey"
    >
      <template #item="{ item: group }">
        <button
          class="group-card"
          type="button"
          :data-testid="`group-${group.id}`"
          data-photo-image
          :data-group-id="group.id"
          :data-image-id="group.coverImageUuid"
          :disabled="!props.canOpenScan"
          @click="controller.selectGroup(groupLookupId(group))"
        >
          <span
            class="photo"
            :class="{
              'image-loaded': coverRetry.isLoaded(group),
              'image-error': coverRetry.isErrored(group),
            }"
            :data-image-error="coverRetry.isErrored(group) ? 'true' : undefined"
          >
            <span class="photo-skeleton" aria-hidden="true" />
            <img
              v-if="group.coverUrl && !coverRetry.isErrored(group)"
              class="sentry-block"
              :src="coverRetry.src(group)"
              alt=""
              loading="lazy"
              @load="coverRetry.onLoad(group)"
              @error="coverRetry.onError(group)"
            />
          </span>
          <span class="name">{{ group.name }}</span>
        </button>
      </template>
    </EntrGridPanel>

    <button class="fallback reveal" style="--reveal-order: 3" type="button" data-testid="open-scan" :disabled="!props.canOpenScan" @click="controller.openScan">
      <EntrIcon name="qr" />
      <span>{{ t('attract.scanFallback') }}</span>
    </button>

    <EntrChrome :step="1" :show-progress="false" @help="controller.help" />
  </section>

  <section
    v-else
    class="screen attract-screen attract-scan-only"
    data-testid="screen-attract"
  >
    <button
      type="button"
      class="scan-target"
      data-testid="open-scan"
      :disabled="!props.canOpenScan"
      @click="controller.openScan"
    >
      <header class="intro">
        <h1 class="t-headline headline reveal" style="--reveal-order: 0">{{ t('attract.headline') }}</h1>
        <p class="t-body-lg subhead reveal" style="--reveal-order: 1">{{ t('scan.title') }}</p>
      </header>
      <div class="scan-prompt">
        <div class="qr-mark reveal" style="--reveal-order: 2">
          <EntrIcon name="qr" />
        </div>
        <p class="t-body-lg cta reveal" style="--reveal-order: 3">{{ t('attract.scanFallback') }}</p>
      </div>
    </button>
    <EntrChrome :step="1" :show-progress="false" @help="controller.help" />
  </section>
</template>

<style scoped>
.attract-browse {
  display: grid;
  grid-template-rows: 416px 1172px minmax(0, 1fr);
  padding: 0 var(--entr-side);
}

.intro {
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: center;
  padding-top: 200px;
  text-align: center;
}

.subhead {
  color: var(--entr-text-soft);
}

.group-grid {
  min-width: 0;
  min-height: 0;
}

.group-card {
  display: grid;
  gap: 16px;
  touch-action: manipulation;
}

.photo {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
  border-radius: var(--entr-radius-photo);
  background: var(--entr-ink);
  transition: transform 150ms ease-out;
}

.group-card:active .photo {
  transform: scale(0.98);
}

.photo img {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.08);
  opacity: 0;
  transition: opacity 220ms var(--entr-ease);
}

.photo.image-loaded img {
  opacity: 1;
}

.photo-skeleton {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  border-radius: inherit;
  background:
    linear-gradient(
      135deg,
      rgba(47, 40, 210, 0.32),
      rgba(14, 18, 69, 0.92)
    ),
    var(--entr-ink);
}

.photo-skeleton::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -70%;
  width: 70%;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.28),
    transparent
  );
  transform: skewX(-12deg);
  animation: photo-shimmer 1.45s ease-in-out infinite;
}

.photo-skeleton::after {
  position: absolute;
  inset: 0;
  width: min(46%, 220px);
  height: 54px;
  margin: auto;
  content: "";
  background: url("../../assets/brand/entr-logo.svg") center / contain
    no-repeat;
  opacity: 0;
  transition: opacity 180ms var(--entr-ease);
}

.photo.image-error .photo-skeleton::before {
  animation: none;
  opacity: 0;
}

.photo.image-error .photo-skeleton::after {
  opacity: 0.68;
}

.name {
  max-height: 80px;
  color: var(--entr-text);
  font-size: 32px;
  line-height: 1.2;
  font-weight: 500;
  text-align: center;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.fallback {
  display: flex;
  gap: 12px;
  align-items: center;
  align-self: start;
  justify-content: center;
  justify-self: center;
  margin-top: 16px;
  color: var(--entr-text-muted);
  font-size: 26px;
  white-space: nowrap;
}

.fallback :deep(svg) {
  width: 28px;
  height: 28px;
}

.attract-scan-only {
  display: grid;
}

.attract-scan-only .scan-target {
  display: grid;
  grid-template-rows: 416px minmax(0, 1fr);
  width: 100%;
  height: 100%;
  padding: 0 80px;
  touch-action: manipulation;
  background: transparent;
  border: none;
  cursor: pointer;
  color: inherit;
}

.attract-scan-only .scan-prompt {
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  padding-top: 342px;
}

.attract-scan-only .qr-mark :deep(svg) {
  width: 320px;
  height: 320px;
  color: var(--entr-branding-blue);
}

.attract-scan-only .cta {
  color: var(--entr-text-muted);
  text-align: center;
}

@keyframes photo-shimmer {
  0% {
    transform: translateX(0) skewX(-12deg);
  }

  100% {
    transform: translateX(250%) skewX(-12deg);
  }
}
</style>
